<!-- ORGANISM: AccountMenu.vue -->

<script setup lang="ts">
//
// Imports
//
import AppBarActionButton from "../molecules/AppBarActionButton.vue";
import AccountMenuCard from "./AccountMenuCard.vue";

//
// Composables
//

// Auth
const { userName, userEmail, userInitials, logout, isOtpPatient } = useAuth();

// For OTP patients, resolve gender from loaded visit results or balance
const { currentVisitResults } = useVisits("otp-results");
const { currentVisitBalance } = useVisits("otp-balance");

/**
 * Shared gender flag for OTP patients.
 * Persisted across both results and balance page navigations so the avatar
 * colour survives even when visit data is unavailable (e.g. payment-required
 * 404 on the results endpoint where currentVisitResults is null).
 */
const otpPatientIsMale = useState<boolean | null>("ui.otpPatientIsMale", () => null);

// Populate the shared gender flag from whichever source loads first
watch(
  () => currentVisitResults.value?.patient?.isMale,
  (val) => {
    if (val !== undefined) otpPatientIsMale.value = val;
  },
  { immediate: true },
);
watch(
  () => currentVisitBalance.value?.patient?.isMale,
  (val) => {
    if (val !== undefined) otpPatientIsMale.value = val;
  },
  { immediate: true },
);

// Localization
const { t } = useI18n();

//
// State
//
const accountMenuOpen = ref(false);

//
// Computed
//

/**
 * Avatar background colour.
 * OTP patients: gender-derived from their visit results/balance data.
 * Corporate (staff) users: always "primary" — the staff JWT contains no gender
 * field, and the account menu avatar represents the logged-in user, not any
 * patient currently being viewed.
 */
const avatarColor = computed(() => {
  if (isOtpPatient.value) {
    const patient = currentVisitResults.value?.patient ?? currentVisitBalance.value?.patient;
    if (patient) return patient.isMale ? "blue-lighten-2" : "pink-lighten-2";
    // Fallback: persisted gender from a previous visit data load
    if (otpPatientIsMale.value !== null) {
      return otpPatientIsMale.value ? "blue-lighten-2" : "pink-lighten-2";
    }
  }

  return "primary";
});
</script>

<template>
  <v-menu v-model="accountMenuOpen" location="bottom" :close-on-content-click="false">
    <!-- Activator -->
    <template #activator="{ props }">
      <app-bar-action-button
        v-bind="props"
        icon="mdi-account-outline"
        :tooltip-text="t('components.common.app-bar.app_bar_actions.account_menu.tooltip')"
      />
    </template>

    <!-- Menu Content -->
    <account-menu-card
      :user-name="userName"
      :user-email="userEmail"
      :user-initials="userInitials"
      :avatar-color="avatarColor"
      :logout="logout"
    />
  </v-menu>
</template>
